import { c as _c } from "react/compiler-runtime";
import { File, FileImage } from 'lucide-react';
import type { SimpleIcon } from 'simple-icons';
import { siAstro, siC, siCplusplus, siCss, siDart, siDocker, siElixir, siGit, siGnubash, siGo, siGraphql, siHaskell, siHtml5, siJavascript, siJson, siKotlin, siLua, siMarkdown, siMdx, siNpm, siPhp, siPnpm, siPostgresql, siPrisma, siPython, siReact, siRuby, siRust, siSass, siScala, siSharp, siSvelte, siSvg, siSwift, siTerraform, siToml, siTypescript, siVuedotjs, siYaml, siYarn, siZig } from 'simple-icons';
import { cn } from '@/lib/utils';
type PrV2FileTypeIconProps = {
  filename: string;
  className?: string;
};
const EXTENSION_GLYPHS: Record<string, SimpleIcon> = {
  ts: siTypescript,
  mts: siTypescript,
  cts: siTypescript,
  tsx: siReact,
  js: siJavascript,
  mjs: siJavascript,
  cjs: siJavascript,
  jsx: siReact,
  css: siCss,
  scss: siSass,
  sass: siSass,
  html: siHtml5,
  htm: siHtml5,
  json: siJson,
  jsonc: siJson,
  md: siMarkdown,
  markdown: siMarkdown,
  mdx: siMdx,
  yaml: siYaml,
  yml: siYaml,
  sql: siPostgresql,
  sh: siGnubash,
  bash: siGnubash,
  zsh: siGnubash,
  py: siPython,
  rs: siRust,
  go: siGo,
  rb: siRuby,
  php: siPhp,
  swift: siSwift,
  kt: siKotlin,
  kts: siKotlin,
  c: siC,
  h: siC,
  cpp: siCplusplus,
  cc: siCplusplus,
  cxx: siCplusplus,
  hpp: siCplusplus,
  cs: siSharp,
  dart: siDart,
  vue: siVuedotjs,
  svelte: siSvelte,
  astro: siAstro,
  graphql: siGraphql,
  gql: siGraphql,
  prisma: siPrisma,
  toml: siToml,
  lua: siLua,
  tf: siTerraform,
  svg: siSvg,
  ex: siElixir,
  exs: siElixir,
  scala: siScala,
  hs: siHaskell,
  zig: siZig
};

// Exact basename matches take precedence over extension matches (e.g. package.json → npm).
const FILENAME_GLYPHS: Record<string, SimpleIcon> = {
  'dockerfile': siDocker,
  '.gitignore': siGit,
  '.gitattributes': siGit,
  '.gitmodules': siGit,
  'package.json': siNpm,
  'package-lock.json': siNpm,
  '.npmrc': siNpm,
  'pnpm-lock.yaml': siPnpm,
  'pnpm-workspace.yaml': siPnpm,
  'yarn.lock': siYarn
};
const IMAGE_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'avif', 'ico', 'bmp']);
function getBasename(filename: string): string {
  const slashIndex = filename.lastIndexOf('/');
  return slashIndex === -1 ? filename : filename.slice(slashIndex + 1);
}
function getExtension(basename: string): string {
  const dotIndex = basename.lastIndexOf('.');
  // Dotfiles like ".env" have no extension in the file-type sense
  return dotIndex <= 0 ? '' : basename.slice(dotIndex + 1);
}

// Guard with Object.hasOwn: filenames like "constructor" or "toString" would otherwise
// match inherited Object.prototype members and return a non-glyph value.
function lookupGlyph(glyphs: Record<string, SimpleIcon>, key: string): SimpleIcon | undefined {
  return Object.hasOwn(glyphs, key) ? glyphs[key] : undefined;
}
export function PrV2FileTypeIcon(t0) {
  const $ = _c(16);
  const {
    filename,
    className
  } = t0;
  let glyph;
  let t1;
  if ($[0] !== className || $[1] !== filename) {
    t1 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const basename = getBasename(filename).toLowerCase();
      const extension = getExtension(basename);
      glyph = lookupGlyph(FILENAME_GLYPHS, basename) ?? lookupGlyph(EXTENSION_GLYPHS, extension);
      if (!glyph) {
        const FallbackIcon = IMAGE_EXTENSIONS.has(extension) ? FileImage : File;
        let t2;
        if ($[4] !== className) {
          t2 = cn("text-zinc-500", className);
          $[4] = className;
          $[5] = t2;
        } else {
          t2 = $[5];
        }
        let t3;
        if ($[6] !== FallbackIcon || $[7] !== t2) {
          t3 = <FallbackIcon aria-hidden="true" className={t2} />;
          $[6] = FallbackIcon;
          $[7] = t2;
          $[8] = t3;
        } else {
          t3 = $[8];
        }
        t1 = t3;
        break bb0;
      }
    }
    $[0] = className;
    $[1] = filename;
    $[2] = glyph;
    $[3] = t1;
  } else {
    glyph = $[2];
    t1 = $[3];
  }
  if (t1 !== Symbol.for("react.early_return_sentinel")) {
    return t1;
  }
  let t2;
  if ($[9] !== className) {
    t2 = cn("text-zinc-400", className);
    $[9] = className;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] !== glyph.path) {
    t3 = <path d={glyph.path} />;
    $[11] = glyph.path;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] !== t2 || $[14] !== t3) {
    t4 = <svg viewBox="0 0 24 24" aria-hidden="true" fill="currentColor" className={t2}>{t3}</svg>;
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  return t4;
}